import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, AlertTriangle, Gamepad2, Wallet, Scale, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Terms = () => {
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  const toggleSection = (section: string) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };

  const sections = [
    {
      id: 'eligibility',
      icon: <Shield className="w-6 h-6 text-[#00fff5]" />,
      title: '1. Eligibility',
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">You must be at least 18 years of age to use TheArenaX. By using our platform, you represent and warrant that:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
            <li>You are 18 years of age or older</li>
            <li>You have the legal capacity to enter into binding contracts</li>
            <li>You are NOT a resident of Telangana, Odisha, or Assam</li>
            <li>You will use the platform in compliance with all applicable laws</li>
          </ul>
          <div className="bg-[#EF444420] border border-[#EF4444] rounded-lg p-4 mt-4">
            <p className="text-[#EF4444] font-rajdhani font-bold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              ⚠️ STRICT PROHIBITION: Residents of Telangana, Odisha, and Assam cannot use TheArenaX
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'skill-based',
      icon: <Gamepad2 className="w-6 h-6 text-[#00fff5]" />,
      title: '2. Skill-Based Gaming',
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">TheArenaX hosts skill-based tournaments for BGMI and Free Fire. Winners are determined by:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
            <li>In-game performance (kills, survival time, match placement)</li>
            <li>Player skill, strategy, and gameplay - NOT chance or luck</li>
            <li>Objective performance metrics tracked by our system</li>
          </ul>
          <div className="bg-[#00E67620] border border-[#00E676] rounded-lg p-4 mt-4">
            <p className="text-[#00E676] font-rajdhani font-bold">✓ Fully compliant with Indian gaming laws and Supreme Court guidelines on skill-based gaming</p>
          </div>
        </div>
      )
    },
    {
      id: 'coins',
      icon: <Wallet className="w-6 h-6 text-[#00fff5]" />,
      title: '3. Coin System & Payments',
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">Our coin system operates as follows:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
            <li>Coins are virtual currency used for tournament entry</li>
            <li>Coin purchases are FINAL and NON-REFUNDABLE</li>
            <li>Minimum withdrawal: 25 coins (₹25) via UPI</li>
            <li>Withdrawals processed within 24-48 hours</li>
            <li>TDS applicable as per Income Tax Act, 1961 (30% on winnings above ₹10,000)</li>
            <li>PAN card required for withdrawals above ₹10,000</li>
          </ul>
        </div>
      )
    },
    {
      id: 'prohibited',
      icon: <AlertTriangle className="w-6 h-6 text-[#EF4444]" />,
      title: '4. Prohibited Conduct',
      content: (
        <div className="space-y-4">
          <p className="text-gray-300 font-bold text-[#EF4444]">ZERO TOLERANCE POLICY:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
            <li>Cheating, hacking, or using unauthorized tools</li>
            <li>Creating multiple accounts</li>
            <li>Match-fixing or unfair teaming</li>
            <li>Abusive language or harassment</li>
            <li>Account sharing or selling</li>
            <li>Any form of fraud or manipulation</li>
          </ul>
          <div className="bg-[#EF444420] border border-[#EF4444] rounded-lg p-4 mt-4">
            <p className="text-[#EF4444] font-rajdhani font-bold">⚠️ Violators face immediate permanent ban and forfeiture of all winnings</p>
          </div>
        </div>
      )
    },
    {
      id: 'liability',
      icon: <Scale className="w-6 h-6 text-[#00fff5]" />,
      title: '5. Limitation of Liability',
      content: (
        <div className="space-y-4">
          <p className="text-gray-300">TheArenaX shall not be liable for:</p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
            <li>Technical issues with BGMI/Free Fire servers</li>
            <li>Internet connectivity problems on your end</li>
            <li>Device malfunctions</li>
            <li>Loss of data due to user error</li>
          </ul>
          <p className="text-gray-300 mt-4">Maximum liability limited to the amount of coins in your wallet at the time of claim.</p>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1E]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#00fff5] rounded-full filter blur-3xl opacity-5"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#7000ff] rounded-full filter blur-3xl opacity-5"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-block p-4 bg-gradient-to-r from-[#00fff5] to-[#7000ff] rounded-2xl mb-6">
            <Scale className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-rajdhani font-bold text-white mb-4">
            Terms of <span className="text-[#00fff5]">Use</span>
          </h1>
          <p className="text-xl text-gray-400 font-rajdhani mb-8">Last Updated: March 2026</p>
          <div className="bg-[#1E2338] border border-[#2A2F45] rounded-2xl p-6 inline-block">
            <p className="text-gray-300 font-rajdhani">⚠️ By using TheArenaX, you agree to these Terms & Conditions</p>
          </div>
        </div>
      </div>

      {/* Terms Content */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        <div className="space-y-4">
          {sections.map((section) => (
            <div key={section.id} className="bg-[#1E2338] border border-[#2A2F45] rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full p-6 flex items-center justify-between hover:bg-[#2A2F45] transition"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-[#00fff510] rounded-lg">
                    {section.icon}
                  </div>
                  <span className="text-white font-rajdhani font-bold text-lg">{section.title}</span>
                </div>
                {expandedSections.includes(section.id) ? (
                  <ChevronUp className="w-5 h-5 text-[#00fff5]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {expandedSections.includes(section.id) && (
                <div className="px-6 pb-6">
                  <div className="h-px bg-[#2A2F45] mb-4"></div>
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Section */}
        <div className="mt-12 bg-gradient-to-r from-[#00fff510] to-[#7000ff10] border border-[#2A2F45] rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-rajdhani font-bold text-white mb-4">Questions About Our Terms?</h3>
          <p className="text-gray-400 mb-6">Our legal team is here to help</p>
          <a 
            href="mailto:support@lastzoneindia.com"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00fff5] to-[#7000ff] px-8 py-3 rounded-xl text-white font-rajdhani font-bold hover:opacity-90 transition"
          >
            <Mail className="w-5 h-5" />
            support@lastzoneindia.com
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Terms;